import { useEffect } from "react";

import isNotNullish from "lib/isNotNullish";
import { ProductSectionView } from "AssortmentOverview/ProductSection/types";
import { useActiveTab } from "components/PurchaseOrder";
import { useSearchParam } from "lib/SearchParams";

const layoutName = "layout";
const defaultLayout = ProductSectionView.LIST_PRODUCT;

const isValidProductSectionView = (value: string | null) =>
  isNotNullish(value) &&
  Object.values(ProductSectionView).includes(value as ProductSectionView);

const useStoredLayout = () => {
  const storedLayoutValue = window.localStorage.getItem(layoutName);
  const storedLayout = isValidProductSectionView(storedLayoutValue)
    ? (storedLayoutValue as ProductSectionView)
    : null;
  const setStoredLayout = (value: ProductSectionView) =>
    window.localStorage.setItem(layoutName, value);
  return [storedLayout, setStoredLayout] as const;
};

const useLayoutParam = () => {
  const [layoutParamValue, setLayoutParamValue] = useSearchParam(layoutName);
  const layoutParam = isValidProductSectionView(layoutParamValue)
    ? (layoutParamValue as ProductSectionView)
    : null;
  const setLayoutParam = (value: ProductSectionView | null) =>
    setLayoutParamValue(value);
  return [layoutParam, setLayoutParam] as const;
};

export const useActiveLayout = () => {
  const [layoutParam, setLayoutParam] = useLayoutParam();
  const [storedLayout, setStoredLayout] = useStoredLayout();

  /**
   * This effect is used to propagate the value of the search param into the
   * local storage.
   */
  useEffect(() => {
    if (layoutParam && layoutParam !== storedLayout) {
      setStoredLayout(layoutParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutParam, storedLayout]);

  /**
   * This effect has a dual purpose:
   * 1. To clear the search param when it is not the workspace products page.
   *    This is so that the search param does not show in the url bar when it
   *    is not relevant.
   * 2. To set the search param to the stored value if the param is not set.
   */
  const activeTab = useActiveTab();
  useEffect(() => {
    if (activeTab === "overview") {
      setLayoutParam(null);
    } else if (!layoutParam && storedLayout) {
      setLayoutParam(storedLayout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const activeLayout = layoutParam ?? storedLayout ?? defaultLayout;
  const setActiveLayout = (layout: ProductSectionView) => {
    setLayoutParam(layout);
    setStoredLayout(layout);
  };
  return [activeLayout, setActiveLayout] as const;
};
